import { SearchOutlined } from '@ant-design/icons';
import { Card, Input, Tabs } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type WorkflowSummary, workflowsApi } from '@/api/workflows';
import { PendingQueue } from '@/approver/PendingQueue';
import { PublishedWorkflows } from '@/approver/PublishedWorkflows';
import { RejectedWorkflows } from '@/approver/RejectedWorkflows';
import { useFetchedList } from '@/hooks/useFetchedList';
import { useRememberedTab } from '@/hooks/useRememberedTab';
import { handleApiError } from '@/lib/handleApiError';
import { message } from '@/lib/message';

const APPROVER_TAB_KEY = 'bmo-inflow.approver.activeTab';
// Audit history (`recent`) lives on its own page now — see
// `/approver/audit-history`. Header-menu clicks route there directly
// instead of switching tabs. Legacy 'recent' in localStorage is
// migrated to 'published' below.
const TAB_KEYS = ['published', 'approved', 'queue', 'rejected'] as const;
type TabKey = (typeof TAB_KEYS)[number];

/**
 * Approver dashboard. User identity / sign-out / role live in the app header.
 *
 * Tab order: Published Workflows / Approved Workflows / Pending Queue / Recent
 * Decisions. Tab titles carry the count of items inside so the user can see
 * at-a-glance "5 things waiting for me to approve" without clicking through.
 *
 * Data sources are all owned here so the counts come for free and so a future
 * "refresh dashboard" affordance can hit one place. The shared workflows list
 * also lets the Published/Approved tabs stay in sync — toggling Publish on one
 * tab immediately updates the other instead of leaving a stale copy.
 *
 * Active tab is persisted to localStorage so a refresh keeps the user where
 * they were.
 */
export function ApproverDashboard() {
  const { t, i18n } = useTranslation();

  const [activeKey, setActiveKey] = useRememberedTab<TabKey>(
    APPROVER_TAB_KEY,
    TAB_KEYS,
    'published',
    // Legacy `recent` value → land on `published` now that the audit
    // history has its own page. Users who last visited the Recent tab
    // don't get stuck on a no-op reset.
    (raw) => (raw === 'recent' ? 'published' : undefined),
  );
  const handleChange = (k: string) => {
    if ((TAB_KEYS as readonly string[]).includes(k)) setActiveKey(k as TabKey);
  };

  // Search query for the dashboard. Substring match (case-insensitive)
  // over the names + app-name + (where available) owner / approver /
  // rejecter display names. Drives both the visible list per tab AND
  // the "(N)" badge on each tab title so the approver can see at a
  // glance which tabs hold hits.
  const [query, setQuery] = useState('');

  // Three independent feeds — the Approver dashboard surfaces three views the BE
  // doesn't roll up into a single endpoint. Failing one shouldn't block the others.
  // useFetchedList owns the cancellation flag + error toast + loading state per feed.
  const {
    data: workflows,
    loading: workflowsLoading,
    setData: setWorkflows,
  } = useFetchedList(() => workflowsApi.list(), t, 'workflow.list.loadFailed');
  // Drop the optimistic local order in the Published tab + refetch the canonical
  // list after a successful drag-reorder. useFetchedList doesn't expose its
  // own reload, so we re-run the same query and patch setData.
  const reloadWorkflows = useCallback(async () => {
    try {
      setWorkflows(await workflowsApi.list());
    } catch (err) {
      handleApiError(err, t, t('workflow.list.loadFailed'));
    }
  }, [setWorkflows, t]);
  // Archive retires an Approved / Rejected / already-published workflow (a
  // published one stays archivable while a new version is in flight as a
  // Draft — that was the 2026-08-01 bug). The card vanishes from
  // every tab (the BE drops archived rows from the working lists), so a full
  // reload is the honest refresh here rather than a local splice.
  const handleArchive = useCallback(
    async (id: string) => {
      try {
        await workflowsApi.archive(id);
        await reloadWorkflows();
      } catch (err) {
        handleApiError(err, t, t('workflow.archive.archiveFailed'));
      }
    },
    [reloadWorkflows, t],
  );

  const { data: queue, loading: queueLoading } = useFetchedList(
    () => workflowsApi.queue(),
    t,
    'approver.queue.loadFailed',
  );

  const handleTogglePublished = useCallback(
    async (id: string, isActive: boolean) => {
      try {
        // Pass session language so the "your workflow is now live" email
        // (AC6 on the Publish story) renders in the approver's locale on
        // the activate branch. Deactivate silently drops the arg — no
        // email fires there.
        const updated = await workflowsApi.setActive(id, isActive, i18n.language);
        // Replace the touched row so each tab's local filter (isActive=true/false)
        // moves it to the right side without a refresh.
        setWorkflows((prev) => prev?.map((w) => (w.id === id ? updated : w)) ?? prev);
        message.success(
          isActive ? t('workflow.list.activatedSuccess') : t('workflow.list.deactivatedSuccess'),
        );
      } catch (err) {
        handleApiError(err, t, t('workflow.list.activeFailed'));
      }
    },
    [t, i18n.language, setWorkflows],
  );

  const handlePromotePending = useCallback(
    async (id: string) => {
      try {
        const updated = await workflowsApi.promotePendingApproval(id, i18n.language);
        setWorkflows((prev) => prev?.map((w) => (w.id === id ? updated : w)) ?? prev);
        message.success(t('workflow.list.activatedSuccess'));
      } catch (err) {
        handleApiError(err, t, t('workflow.list.activeFailed'));
      }
    },
    [t, i18n.language, setWorkflows],
  );

  // Counts surface in the tab titles. Workflows feed counts split on isActive;
  // queue / decisions counts come straight from their respective arrays.
  // 2026-06-29 staging: a workflow with a pendingApprovedVersionNumber appears
  // in BOTH Published (showing v1) AND Approved (showing v2 awaiting promote).
  const needle = query.trim().toLowerCase();
  const searching = needle.length > 0;

  // Per-feed substring filter. WorkflowSummary covers Published / Approved /
  // Pending; WorkflowDecision covers Recent. Both short-circuit to "match
  // everything" when the search box is empty so the unfiltered render path
  // costs nothing extra.
  const matchTemplate = useMemo(() => {
    if (!searching) return () => true;
    return (w: WorkflowSummary) =>
      w.nameEn.toLowerCase().includes(needle) ||
      w.nameFr.toLowerCase().includes(needle) ||
      w.appName.toLowerCase().includes(needle) ||
      w.descriptionEn.toLowerCase().includes(needle) ||
      w.descriptionFr.toLowerCase().includes(needle) ||
      w.ownerName.toLowerCase().includes(needle);
  }, [needle, searching]);

  // Filtered slices passed down to the child lists when the search is
  // active. Each child component already does its own mode-specific
  // post-filter (e.g. PublishedWorkflows splits on isActive), so passing
  // the already-narrowed list keeps the search authoritative without
  // teaching the children about it.
  const filteredWorkflows = useMemo(
    () => (workflows ?? []).filter(matchTemplate),
    [workflows, matchTemplate],
  );
  const filteredQueue = useMemo(() => (queue ?? []).filter(matchTemplate), [queue, matchTemplate]);

  // Tab counts. When searching, the "(N)" badge shows match-count alone
  // (matches the originator dashboard's convention) so the approver can
  // tell at a glance which tabs hold hits without flipping through.
  const published = filteredWorkflows.filter(
    (w) => w.currentVersionNumber > 0 && w.isActive,
  ).length;
  const approved = filteredWorkflows.filter(
    (w) => w.currentVersionNumber > 0 && (!w.isActive || w.pendingApprovedVersionNumber),
  ).length;
  const pending = filteredQueue.length;
  // 2026-07-15: BMO asked for the Rejected tab back. Currently-Rejected only —
  // once the originator resubmits, the card moves back to the Pending Queue and
  // drops off this tab. Historical rejections remain in Audit History.
  const rejected = filteredWorkflows.filter((w) => w.status === 'Rejected').length;
  const tabLabel = (key: string, count: number) => `${t(`approver.tabs.${key}`)} (${count})`;

  return (
    <Card title={t('dashboard.approver.title')}>
      <Tabs
        activeKey={activeKey}
        onChange={handleChange}
        tabBarExtraContent={
          // AntD's `tabBarExtraContent` slot sits flush with the tab
          // strip's bottom underline — wrap in a div with a small
          // bottom offset so the input has breathing room above the
          // divider. Pill radius marks it as a search affordance
          // (matches the originator dashboard).
          <div style={{ paddingBottom: 8 }}>
            <Input
              allowClear
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('workflow.list.searchPlaceholder')}
              prefix={<SearchOutlined style={{ color: '#6b7280' }} />}
              style={{ width: 280, borderRadius: 999 }}
            />
          </div>
        }
        items={[
          {
            key: 'published',
            label: tabLabel('published', published),
            children: (
              <PublishedWorkflows
                mode="published"
                items={searching ? filteredWorkflows : workflows}
                loading={workflowsLoading}
                onTogglePublished={handleTogglePublished}
                onPromotePending={handlePromotePending}
                onReordered={reloadWorkflows}
                onArchive={handleArchive}
                searchQuery={query}
                onClearSearch={() => setQuery('')}
              />
            ),
          },
          {
            key: 'approved',
            label: tabLabel('approved', approved),
            children: (
              <PublishedWorkflows
                mode="approved"
                items={searching ? filteredWorkflows : workflows}
                loading={workflowsLoading}
                onTogglePublished={handleTogglePublished}
                onPromotePending={handlePromotePending}
                onReordered={reloadWorkflows}
                onArchive={handleArchive}
                searchQuery={query}
                onClearSearch={() => setQuery('')}
              />
            ),
          },
          {
            key: 'queue',
            label: tabLabel('pending', pending),
            children: (
              <PendingQueue
                items={searching ? filteredQueue : queue}
                loading={queueLoading}
                searchQuery={query}
                onClearSearch={() => setQuery('')}
              />
            ),
          },
          {
            key: 'rejected',
            label: tabLabel('rejected', rejected),
            children: (
              <RejectedWorkflows
                items={searching ? filteredWorkflows : workflows}
                loading={workflowsLoading}
                onArchive={handleArchive}
                searchQuery={query}
                onClearSearch={() => setQuery('')}
              />
            ),
          },
        ]}
      />
    </Card>
  );
}
